/**
 * <ActivitySearchBar> — la recherche des activités faites, en tête de liste.
 *
 * Trois états, comme la recherche de WhatsApp :
 *  - au repos, la barre seule ;
 *  - au toucher de la barre, les filtres s'ouvrent dessous, chacun avec son icône — le type, la
 *    matière (son picto, celui de `<SubjectBadge>`), la période ; « OK » les referme ;
 *  - refermée avec un filtre posé, ce filtre reste en puce sous la barre, pour qu'on sache que la
 *    liste est filtrée. La toucher le retire.
 *
 * Une puce par groupe à la fois ; la toucher de nouveau la retire. Tout se combine.
 *
 * Contrôlé : la recherche arrive en `value` et repart par `onChange` — c'est l'écran qui filtre.
 * L'ouverture du panneau, elle, ne regarde que la barre : elle reste ici.
 */
import React, { useState } from "react"
import { Keyboard } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { TextButton } from "../../atoms/Buttons/TextButton"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { HorizontalList } from "../../atoms/HorizontalList"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { SubjectBadge } from "../../atoms/SubjectBadge"
import { TextInput } from "../../atoms/TextInput"
import { Text } from "../../atoms/typography/Text"
import {
    ACTIVITY_PERIOD_FILTERS,
    ACTIVITY_TYPE_FILTERS,
    EMPTY_ACTIVITY_SEARCH,
    isActivitySearchActive,
    toggleActivitySearch,
} from "../../../ports/primary/ui/formatters/activitySearchFormatter"
import { type UIActivitySearch, type UISubject } from "../../../ports/primary/ui/types"

export type ActivitySearchBarProps = {
    value: UIActivitySearch
    onChange: (next: UIActivitySearch) => void
    /** Les matières qu'on peut choisir — celles de l'élève. */
    subjects: UISubject[]
    /** Le nombre d'activités qui passent, affiché dès qu'un filtre est posé. */
    resultCount?: number
    /** Les filtres ouverts d'emblée — pour les stories ; à l'écran, c'est le toucher qui les ouvre. */
    defaultOpen?: boolean
    testID?: string
}

export function ActivitySearchBar(props: ActivitySearchBarProps) {
    const { value, onChange, subjects, resultCount, defaultOpen = false, testID } = props

    const [open, setOpen] = useState(defaultOpen)
    // Refermer, c'est aussi baisser le clavier : le champ perd le focus, et le toucher suivant
    // sur la barre rouvre les filtres.
    const close = () => {
        setOpen(false)
        Keyboard.dismiss()
    }
    const active = isActivitySearchActive(value)

    const type = ACTIVITY_TYPE_FILTERS.find((f) => f.key === value.type)
    const subject = subjects.find((s) => String(s.id) === String(value.subjectId))
    const period = ACTIVITY_PERIOD_FILTERS.find((f) => f.key === value.period)

    return (
        <Stack gap="3" testID={testID}>
            <Row gap="2" align="center">
                <Stack style={styles.field}>
                    <TextInput
                        value={value.query}
                        onChangeText={(query) => onChange({ ...value, query })}
                        onFocus={() => setOpen(true)}
                        onSubmitEditing={close}
                        maxLength={MAX_QUERY_LENGTH}
                        placeholder="Rechercher une activité"
                        accessibilityLabel="Rechercher une activité"
                        returnKeyType="search"
                        left={<Icon name="search" size={18} color="text-muted" />}
                        right={
                            value.query !== "" ? (
                                <IconButton
                                    iconName="times"
                                    appearance="transparent"
                                    iconColor="text-muted"
                                    size={32}
                                    onPress={() => onChange({ ...value, query: "" })}
                                    accessibilityLabel="Effacer la recherche"
                                />
                            ) : undefined
                        }
                    />
                </Stack>
                {open && <TextButton label="OK" onPress={close} />}
            </Row>

            {open ? (
                <Stack gap="3">
                    <FilterGroup title="Type">
                        {ACTIVITY_TYPE_FILTERS.map((filter) => (
                            <ChoiceChip
                                variant="pill"
                                key={filter.key}
                                label={filter.label}
                                iconName={filter.icon}
                                selected={value.type === filter.key}
                                onPress={() => onChange(toggleActivitySearch(value, "type", filter.key))}
                            />
                        ))}
                    </FilterGroup>
                    {subjects.length > 0 && (
                        <FilterGroup title="Matière">
                            {subjects.map((s) => (
                                <SubjectBadge
                                    key={s.id}
                                    subject={s}
                                    showLabel
                                    size="md"
                                    selected={String(value.subjectId) === String(s.id)}
                                    onSelect={() => onChange(toggleActivitySearch(value, "subjectId", s.id))}
                                />
                            ))}
                        </FilterGroup>
                    )}
                    <FilterGroup title="Période">
                        {ACTIVITY_PERIOD_FILTERS.map((filter) => (
                            <ChoiceChip
                                variant="pill"
                                key={filter.key}
                                label={filter.label}
                                iconName={filter.icon}
                                selected={value.period === filter.key}
                                onPress={() => onChange(toggleActivitySearch(value, "period", filter.key))}
                            />
                        ))}
                    </FilterGroup>
                </Stack>
            ) : (
                (type != null || subject != null || period != null) && (
                    <HorizontalList gap="2" keyboardShouldPersistTaps="handled">
                        {type != null && (
                            <ChoiceChip
                                variant="pill"
                                label={type.label}
                                iconName={type.icon}
                                selected
                                onPress={() => onChange({ ...value, type: null })}
                            />
                        )}
                        {subject != null && (
                            <SubjectBadge
                                subject={subject}
                                showLabel
                                size="md"
                                selected
                                onSelect={() => onChange({ ...value, subjectId: null })}
                            />
                        )}
                        {period != null && (
                            <ChoiceChip
                                variant="pill"
                                label={period.label}
                                iconName={period.icon}
                                selected
                                onPress={() => onChange({ ...value, period: null })}
                            />
                        )}
                    </HorizontalList>
                )
            )}

            {active && (
                <Row justify="between" align="center">
                    <Text size="text-sm" color="text-muted">
                        {resultCount === 1 ? "1 activité" : `${resultCount ?? 0} activités`}
                    </Text>
                    <TextButton label="Tout effacer" size="sm" onPress={() => onChange(EMPTY_ACTIVITY_SEARCH)} />
                </Row>
            )}
        </Stack>
    )
}

function FilterGroup(props: { title: string; children: React.ReactNode }) {
    return (
        <Stack gap="2">
            <Text size="text-xs" weight="semibold" color="text-muted">
                {props.title}
            </Text>
            {/* Clavier levé, le premier toucher choisit le filtre au lieu de seulement baisser le clavier. */}
            <HorizontalList gap="2" keyboardShouldPersistTaps="handled">
                {props.children}
            </HorizontalList>
        </Stack>
    )
}

/** La borne du serveur (`title`, 100 caractères) : au-delà, il refuse la recherche. */
const MAX_QUERY_LENGTH = 100

const styles = StyleSheet.create(() => ({
    field: { flex: 1 },
}))
